import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../../assets/icons/LineDot";
import { StyledLegend } from "../../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import {
  formatMeasurementAverageRangeDates,
  NebulizerChartRow,
} from "../../../../../../../utils/chartUtils";
import {
  getLongFormattedDate,
  getRelativeTimeSinceDate,
} from "../../../../../../../utils/dateFormatter";
import {
  ChartLegendContainer,
  Container,
  DateLabel,
  Divider,
  MainValueContainer,
  MainValueContainerFirst,
  MainValuesContainer,
  NumberLabel,
  TextLabel,
} from "./CustomNebulizerModalChartLegend.style";

type CustomNebulizerModalChartLegendProps = {
  data: NebulizerChartRow[];
  startDate: Dayjs;
  endDate: Dayjs;
  latestTreatmentTime?: Dayjs;
};

export function CustomNebulizerModalChartLegend({
  data,
  startDate,
  endDate,
  latestTreatmentTime,
}: CustomNebulizerModalChartLegendProps) {
  const { t } = useTranslation();

  const [disabled, setDisabled] = useState(false);
  const [averageDuration, setAverageDuration] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<string | null>(null);
  const [numberOfTreatments, setNumberOfTreatments] = useState<number | null>(
    null
  );

  useEffect(() => {
    const hasData = Array.isArray(data) && data.length > 0;
    setDisabled(!hasData);
    if (!hasData) return;

    const isAverageView = Boolean(data.some((d) => d.isAverage));

    const summary = data.reduce(
      (acc, d) => {
        const val = Number(d.totalTreatmentTimeNum);

        let w = 0;
        if (isAverageView) {
          w = Number(d.treatmentsCount ?? 0);
        } else if (Number.isFinite(val)) {
          w = 1;
        }

        if (Number.isFinite(val) && w > 0) {
          acc.weightedTotal += val * w;
          acc.count += w;
        }
        return acc;
      },
      { weightedTotal: 0, count: 0 }
    );

    const avg =
      summary.count > 0
        ? Math.round((summary.weightedTotal / summary.count) * 10) / 10
        : 0;

    setAverageDuration(avg);
    setNumberOfTreatments(summary.count);
    setDateRange(formatMeasurementAverageRangeDates(startDate, endDate));
  }, [data, startDate, endDate]);

  const chartLegendContainer = (
    <ChartLegendContainer>
      <StyledLegend key="Incomplete">
        <LineDot fill="#F5A45E" />
        {t("patientPage_measurementModal_nebulizer_incomplete")}
      </StyledLegend>
      <StyledLegend key="Complete">
        <LineDot fill="#87D5A9" />
        {t("patientPage_measurementModal_nebulizer_complete")}
      </StyledLegend>
    </ChartLegendContainer>
  );

  const mainValuesContainer = (
    <MainValuesContainer>
      <MainValueContainerFirst>
        <NumberLabel disabled={disabled}>
          {latestTreatmentTime
            ? getRelativeTimeSinceDate(
                getLongFormattedDate(latestTreatmentTime.toISOString())
              )
            : "-"}
        </NumberLabel>
        <TextLabel disabled={disabled}>
          {t("patientPage_measurementModal_legend_lastSynced")}
        </TextLabel>
        <DateLabel disabled={disabled}>
          {latestTreatmentTime && dayjs(latestTreatmentTime).isValid()
            ? latestTreatmentTime.format("MMM DD, YYYY")
            : "-"}
        </DateLabel>
      </MainValueContainerFirst>

      <MainValueContainer>
        <NumberLabel disabled={disabled}>
          {averageDuration ? `${averageDuration} (sec)` : "-"}
        </NumberLabel>
        <TextLabel disabled={disabled}>
          {t("patientPage_measurementModal_legend_avgDuration")}
        </TextLabel>
        <DateLabel disabled={disabled}>{dateRange ?? "-"}</DateLabel>
      </MainValueContainer>
      <MainValueContainer>
        <NumberLabel disabled={disabled}>
          {numberOfTreatments ?? "-"}
        </NumberLabel>
        <TextLabel disabled={disabled}>
          {t("patientPage_measurementModal_legend_treatmentCount")}
        </TextLabel>
        <DateLabel disabled={disabled}>{dateRange ?? "-"}</DateLabel>
      </MainValueContainer>
    </MainValuesContainer>
  );

  return (
    <div style={{ bottom: -16, position: "relative" }}>
      <Container>
        {mainValuesContainer}
        <Divider />
        {chartLegendContainer}
      </Container>
    </div>
  );
}
